import React, { useState } from 'react';
import { useCart, getPriceBreakdown, PRICING } from '../../context/CartContext';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { Button } from '../ui/button';
import { ShoppingCart, Trash2, Plus, Minus, Truck, Percent } from 'lucide-react';
import { TSHIRT_COLORS } from '../../config/productImages';

const CartItem = ({ item, onRemove, onUpdateQuantity }) => {
  const colorInfo = TSHIRT_COLORS.find(c => c.hex === item.color) || { name: 'Custom' };
  const breakdown = getPriceBreakdown(item.designs);
  
  return (
    <div className="border rounded-lg p-4 mb-3 bg-white">
      <div className="flex justify-between items-start mb-2">
        <div className="flex gap-3">
          <div 
            className="w-12 h-12 rounded border flex-shrink-0"
            style={{ backgroundColor: item.color }}
          />
          <div>
            <p className="font-semibold text-sm">{colorInfo.name} T-Shirt</p>
            <p className="text-xs text-gray-500">Size: {item.size}</p>
          </div>
        </div>
        <button 
          onClick={() => onRemove(item.id)}
          className="text-gray-400 hover:text-red-500 transition-colors"
        >
          <Trash2 size={16} />
        </button>
      </div>
      
      {/* Price breakdown */}
      <div className="text-xs text-gray-600 mb-3 pl-15">
        {breakdown.map((line) => (
          <div key={`${line.label}-${line.price}`} className="flex justify-between">
            <span>{line.label}</span>
            <span>${line.price.toFixed(2)}</span>
          </div>
        ))}
      </div>
      
      {/* Quantity and total */}
      <div className="flex justify-between items-center pt-2 border-t">
        <div className="flex items-center gap-2">
          <button 
            onClick={() => onUpdateQuantity(item.id, item.quantity - 1)}
            disabled={item.quantity <= 1}
            className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-gray-100 disabled:opacity-50"
          >
            <Minus size={14} />
          </button>
          <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
          <button 
            onClick={() => onUpdateQuantity(item.id, item.quantity + 1)}
            className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-gray-100"
          >
            <Plus size={14} />
          </button>
        </div>
        <p className="font-bold text-sm">
          ${(item.unitPrice * item.quantity).toFixed(2)}
        </p>
      </div>
    </div>
  );
};

// Bundle promotion banner
const BundlePromo = ({ totalQuantity }) => {
  const needsForFreeShipping = Math.max(0, PRICING.freeShippingThreshold - totalQuantity);
  const needsForDiscount = Math.max(0, PRICING.discountThreshold - totalQuantity);
  
  if (needsForDiscount === 0) {
    // Already has discount
    return null;
  }
  
  return (
    <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-lg p-3 mb-4 border border-purple-100">
      <p className="text-xs font-semibold text-purple-800 mb-1">Bundle & Save!</p>
      {needsForFreeShipping > 0 ? (
        <p className="text-xs text-purple-600">
          <Truck size={12} className="inline mr-1" />
          Add {needsForFreeShipping} more shirt{needsForFreeShipping > 1 ? 's' : ''} for <span className="font-bold">FREE shipping!</span>
        </p>
      ) : (
        <p className="text-xs text-purple-600">
          <Percent size={12} className="inline mr-1" />
          Add {needsForDiscount} more shirt{needsForDiscount > 1 ? 's' : ''} for <span className="font-bold">15% off!</span>
        </p>
      )}
    </div>
  );
};

export const CartSheet = ({ onCheckout }) => {
  const { cartItems, removeFromCart, updateCartItemQuantity, getCartItemCount, clearCart, cartTotals } = useCart();
  const [isOpen, setIsOpen] = useState(false);
  
  const itemCount = getCartItemCount();
  
  const handleCheckout = () => {
    setIsOpen(false);
    onCheckout();
  };
  
  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetTrigger asChild>
        <Button 
          variant="outline" 
          className="relative gap-2"
          data-testid="cart-button"
        >
          <ShoppingCart size={18} />
          <span className="hidden sm:inline">Cart</span>
          {itemCount > 0 && (
            <span className="absolute -top-2 -right-2 w-5 h-5 bg-primary text-white text-xs rounded-full flex items-center justify-center">
              {itemCount}
            </span>
          )}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-full sm:max-w-md" data-testid="cart-sheet">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <ShoppingCart size={20} />
            Your Cart ({itemCount} {itemCount === 1 ? 'item' : 'items'})
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-6 flex flex-col h-[calc(100vh-180px)]">
          {cartItems.length === 0 ? (
            <div className="flex-1 flex flex-col items-center justify-center text-gray-500">
              <ShoppingCart size={48} className="mb-4 opacity-30" />
              <p>Your cart is empty</p>
              <p className="text-sm">Add some designs to get started!</p>
            </div>
          ) : (
            <>
              {/* Bundle Promotion Banner */}
              <BundlePromo totalQuantity={cartTotals.totalQuantity} />
              
              <div className="flex-1 overflow-y-auto pr-2">
                {cartItems.map(item => (
                  <CartItem 
                    key={item.id}
                    item={item}
                    onRemove={removeFromCart}
                    onUpdateQuantity={updateCartItemQuantity}
                  />
                ))}
              </div>
              
              <div className="border-t pt-4 mt-4 space-y-2">
                {/* Subtotal */}
                <div className="flex justify-between text-sm">
                  <span>Subtotal</span>
                  <span>${cartTotals.subtotal.toFixed(2)}</span>
                </div>
                
                {/* Discount */}
                {cartTotals.hasDiscount && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span className="flex items-center gap-1">
                      <Percent size={14} />
                      {cartTotals.discountPercent}% Bundle Discount
                    </span>
                    <span>-${cartTotals.discountAmount.toFixed(2)}</span>
                  </div>
                )}
                
                {/* Shipping */}
                <div className="flex justify-between text-sm">
                  <span className="flex items-center gap-1">
                    <Truck size={14} />
                    Shipping
                  </span>
                  {cartTotals.hasFreeShipping ? (
                    <span className="text-green-600 font-medium">FREE</span>
                  ) : (
                    <span>${cartTotals.shipping.toFixed(2)}</span>
                  )}
                </div>
                
                {/* Total */}
                <div className="flex justify-between text-lg font-bold pt-2 border-t">
                  <span>Total</span>
                  <span>${cartTotals.total.toFixed(2)}</span>
                </div>
                
                <Button 
                  onClick={handleCheckout}
                  className="w-full"
                  size="lg"
                  data-testid="checkout-btn"
                >
                  Proceed to Checkout
                </Button>
                
                <button 
                  onClick={clearCart}
                  className="w-full text-sm text-gray-500 hover:text-red-500 transition-colors"
                >
                  Clear Cart
                </button>
              </div>
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default CartSheet;
